import { SessionType } from '@/types/model';
import { randomUuid } from '@/practice/crypto';

const STORAGE_KEY = 'issen-media-session-start-v1';

export type SessionStartAttempt = {
  requestId: string;
  supersedesSessionId?: string;
  sessionOwnershipToken?: string;
};

type StoredSessionStart = SessionStartAttempt & {
  ownerUserId: string;
  sessionType: SessionType;
};

export class SessionStartOwnership {
  private inMemoryRecord: StoredSessionStart | null = null;

  constructor(private readonly storage: Storage | null) {}

  begin(ownerUserId: string, sessionType: SessionType): SessionStartAttempt {
    const existing = this.owned(ownerUserId, sessionType);
    if (existing === null) {
      return this.writePending(ownerUserId, sessionType, undefined, undefined);
    }
    if (
      existing.sessionOwnershipToken === undefined
      || existing.supersedesSessionId !== undefined
    ) {
      return existing;
    }
    return this.writePending(
      ownerUserId,
      sessionType,
      existing.requestId,
      existing.sessionOwnershipToken,
    );
  }

  active(
    ownerUserId: string,
    sessionType: SessionType,
    sessionId: string,
  ): SessionStartAttempt | null {
    const existing = this.owned(ownerUserId, sessionType);
    if (
      existing?.requestId !== sessionId
      || existing.supersedesSessionId !== undefined
      || existing.sessionOwnershipToken === undefined
    ) {
      return null;
    }
    return existing;
  }

  confirm(
    attempt: SessionStartAttempt,
    ownerUserId: string,
    sessionType: SessionType,
    sessionId: string,
    sessionOwnershipToken: string,
  ): void {
    const current = this.read();
    if (
      current?.requestId !== attempt.requestId
      || current.supersedesSessionId !== attempt.supersedesSessionId
      || current.sessionOwnershipToken !== attempt.sessionOwnershipToken
    ) {
      return;
    }
    this.write({
      ownerUserId,
      sessionType,
      requestId: sessionId,
      sessionOwnershipToken,
    });
  }

  clear(): void {
    this.inMemoryRecord = null;
    this.storage?.removeItem(STORAGE_KEY);
  }

  private writePending(
    ownerUserId: string,
    sessionType: SessionType,
    supersedesSessionId: string | undefined,
    sessionOwnershipToken: string | undefined,
  ): StoredSessionStart {
    return this.write({
      ownerUserId,
      sessionType,
      requestId: randomUuid().replace(/-/g, ''),
      supersedesSessionId,
      sessionOwnershipToken,
    });
  }

  private owned(
    ownerUserId: string,
    sessionType: SessionType,
  ): StoredSessionStart | null {
    const record = this.read();
    return (
      record !== null
      && record.ownerUserId === ownerUserId
      && record.sessionType === sessionType
    ) ? record : null;
  }

  private read(): StoredSessionStart | null {
    const serialized = this.storage?.getItem(STORAGE_KEY);
    if (serialized === null || serialized === undefined) {
      return this.inMemoryRecord;
    }
    try {
      const value = JSON.parse(serialized) as Partial<StoredSessionStart>;
      if (
        typeof value.ownerUserId !== 'string'
        || typeof value.sessionType !== 'string'
        || typeof value.requestId !== 'string'
        || (
          value.supersedesSessionId !== undefined
          && typeof value.supersedesSessionId !== 'string'
        )
        || (
          value.sessionOwnershipToken !== undefined
          && typeof value.sessionOwnershipToken !== 'string'
        )
      ) {
        return null;
      }
      this.inMemoryRecord = value as StoredSessionStart;
      return this.inMemoryRecord;
    } catch {
      return null;
    }
  }

  private write(record: StoredSessionStart): StoredSessionStart {
    this.inMemoryRecord = record;
    this.storage?.setItem(STORAGE_KEY, JSON.stringify(record));
    return record;
  }
}
